import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { Plus, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProductionLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], shift: "A", kiln: "", product: "", planned_mt: "", actual_mt: "", remarks: "" });

  const loadLogs = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/production-logs`);
      const json = await res.json();
      if (json.success) setLogs(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/factory/production-logs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, planned_mt: parseFloat(form.planned_mt), actual_mt: parseFloat(form.actual_mt) }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success("Production log added");
        setShowForm(false);
        setForm({ date: new Date().toISOString().split("T")[0], shift: "A", kiln: "", product: "", planned_mt: "", actual_mt: "", remarks: "" });
        loadLogs();
      }
    } catch (e) { toast.error("Failed to add log"); }
  };

  const filtered = logs.filter(
    (l) => l.product?.toLowerCase().includes(search.toLowerCase()) || l.kiln?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="production-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Production Log</h1>
          <p className="text-slate-400 text-sm mt-1">Track daily production across shifts and kilns ({logs.length} entries)</p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors" data-testid="add-production-log">
              <Plus className="w-4 h-4" /> New Entry
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1C2B2B] border-emerald-900/30 text-white">
            <DialogHeader><DialogTitle>Add Production Log</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Date</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Shift</label>
                  <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm">
                    <option value="A">Shift A</option><option value="B">Shift B</option><option value="C">Shift C</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400">Kiln</label>
                <input value={form.kiln} onChange={(e) => setForm({ ...form, kiln: e.target.value })} placeholder="e.g. Kiln 1" className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required />
              </div>
              <div>
                <label className="text-xs text-slate-400">Product</label>
                <input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="e.g. Lion Classic Composite" className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Planned (MT)</label>
                  <input type="number" value={form.planned_mt} onChange={(e) => setForm({ ...form, planned_mt: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Actual (MT)</label>
                  <input type="number" value={form.actual_mt} onChange={(e) => setForm({ ...form, actual_mt: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400">Remarks</label>
                <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" />
              </div>
              <button type="submit" className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="submit-production-log">Save Entry</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input type="text" placeholder="Search by product or kiln..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-[#1C2B2B] border border-emerald-900/30 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-600" data-testid="production-search" />
      </div>

      <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl overflow-hidden">
        <table className="w-full" data-testid="production-log-table">
          <thead>
            <tr className="border-b border-emerald-900/30">
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Shift</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Kiln</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Product</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase">Planned</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase">Actual</th>
              <th className="text-right px-4 py-3 text-xs text-slate-400 uppercase">Efficiency</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="text-center py-8 text-slate-500">No production logs yet. Add your first entry.</td></tr>
            )}
            {filtered.map((log) => (
              <tr key={log.log_id} className="border-b border-emerald-900/20 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-sm text-slate-300">{log.date}</td>
                <td className="px-4 py-3 text-sm"><span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300 text-xs">{log.shift}</span></td>
                <td className="px-4 py-3 text-sm text-white">{log.kiln}</td>
                <td className="px-4 py-3 text-sm text-white">{log.product}</td>
                <td className="px-4 py-3 text-sm text-slate-300 text-right">{log.planned_mt} MT</td>
                <td className="px-4 py-3 text-sm text-white text-right font-medium">{log.actual_mt} MT</td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={`font-medium ${log.efficiency >= 95 ? "text-emerald-400" : log.efficiency >= 90 ? "text-amber-400" : "text-red-400"}`}>{log.efficiency}%</span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-400 max-w-[200px] truncate">{log.remarks || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
